import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import { stylingApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { formatNumber, formatDate } from '../lib/formatters.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business Styling Consoleエラー分析。日次エラー率トレンド・エラー種別テーブル・リトライセッション統計を可視化
 * @graph-connects stylingApi [calls] Styling APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects formatNumber [calls] 数値フォーマット
 * @graph-connects formatDate [calls] 日付フォーマット
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 */
export const Route = createFileRoute('/styling/errors')({
  component: StylingErrorsPage,
});

/** @graph-connects none */
const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
];

/** @graph-connects none */
const ERROR_TYPE_LABELS: Record<string, string> = {
  SELECTED_ITEM_IS_NOT_RENTABLE: '在庫切れ',
  LACK_OF_SELECTED_ITEMS: 'アイテム不足',
  USER_IS_RENTING: '選定済みユーザー',
  USER_IS_FREE_MEMBER: '退会ユーザー',
  NOT_EXISTS_STYLING_COMMENTS: 'コメント未入力',
  USER_REQUEST_ITEM_IS_NOT_SELECTED: 'ワンセレクト未選択',
};

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function StylingErrorsPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');

  const errorsQuery = useQuery({
    queryKey: queryKeys.styling.errors(startDate, endDate),
    queryFn: () => stylingApi.getErrors(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const data = errorsQuery.data;

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('styling.errorAnalysis', 'エラー分析')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-tertiary)]">
            {t('styling.errorDescription', 'スタイリング失敗率とエラー種別の分析')}
          </p>
        </div>
        <DateRangeSelector
          preset={preset}
          onPresetChange={setPreset}
          startDate={startDate}
          endDate={endDate}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* サマリカード */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SimpleKpiCard
            label={t('styling.totalFailures', '失敗件数')}
            value={formatNumber(data.summary.totalFailures)}
          />
          <SimpleKpiCard
            label={t('styling.errorRate', 'エラー率')}
            value={`${(data.summary.errorRate * 100).toFixed(1)}%`}
          />
          <SimpleKpiCard
            label={t('styling.retriedSessions', 'リトライセッション')}
            value={formatNumber(data.retrySessions.totalRetried)}
          />
        </div>
      )}

      {/* 日次エラー率チャート */}
      {data && data.dailyRates.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            {t('styling.dailyErrorRate', '日次エラー率')}
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.dailyRates}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip
                  formatter={
                    ((value: number) => [`${(value * 100).toFixed(1)}%`, 'エラー率']) as never
                  }
                  labelFormatter={formatDate as never}
                />
                <Area
                  type="monotone"
                  dataKey="errorRate"
                  stroke="var(--chart-3)"
                  fill="var(--chart-3)"
                  fillOpacity={0.2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* エラー種別 PieChart */}
        {data && data.errorTypes.length > 0 && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
              {t('styling.errorTypes', 'エラー種別')}
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.errorTypes.map((e, i) => ({
                      name: ERROR_TYPE_LABELS[e.errorType] ?? e.errorType,
                      value: e.count,
                      fill: CHART_COLORS[i % CHART_COLORS.length],
                    }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={
                      (({ name, percent }: { name: string; percent: number }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`) as never
                    }
                  />
                  <Tooltip formatter={((value: number) => formatNumber(value)) as never} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* リトライ分析 */}
        {data && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
              {t('styling.retryAnalysis', 'リトライ分析')}
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-[var(--color-border)]">
                <span className="text-sm text-[var(--color-text-secondary)]">
                  {t('styling.retriedSessions', 'リトライセッション数')}
                </span>
                <span className="font-mono font-bold text-[var(--color-text-primary)]">
                  {formatNumber(data.retrySessions.totalRetried)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[var(--color-border)]">
                <span className="text-sm text-[var(--color-text-secondary)]">
                  {t('styling.avgRetryCount', '平均リトライ回数')}
                </span>
                <span className="font-mono font-bold text-[var(--color-text-primary)]">
                  {data.retrySessions.avgRetryCount !== null
                    ? data.retrySessions.avgRetryCount.toFixed(1)
                    : '-'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* エラー種別テーブル */}
      {data && data.errorTypes.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            {t('styling.errorDetail', 'エラー詳細')}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-2 px-3 text-[var(--color-text-secondary)]">
                    エラー種別
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">件数</th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    スタイリスト数
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    対象ユーザー数
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.errorTypes.map((e) => (
                  <tr
                    key={e.errorType}
                    className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
                  >
                    <td className="py-2 px-3 text-[var(--color-text-primary)]">
                      {ERROR_TYPE_LABELS[e.errorType] ?? e.errorType}
                    </td>
                    <td className="text-right py-2 px-3 font-mono">{formatNumber(e.count)}</td>
                    <td className="text-right py-2 px-3 font-mono">
                      {formatNumber(e.uniqueAccounts)}
                    </td>
                    <td className="text-right py-2 px-3 font-mono">
                      {formatNumber(e.uniqueTargets)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** @graph-connects none */
function SimpleKpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="card animate-fade-in-up">
      <p className="text-xs text-[var(--color-text-muted)] mb-1">{label}</p>
      <p className="text-2xl font-bold text-[var(--color-text-primary)]">{value}</p>
    </div>
  );
}
